import { useState } from 'react'
import { motion } from 'framer-motion'
import { Package, Sparkles, ArrowUpRight } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useBillingSummary, useUsageBreakdown, useFeatureAddons, useRequestAddon, usePlanBadgeColor } from '@/hooks/useBilling'

type Tab = 'plan' | 'features'

export default function Subscription() {
  const { user } = useAuthStore()
  const tenantId = user?.tenant_id || ''
  const [activeTab, setActiveTab] = useState<Tab>('plan')

  const { data: summary, isLoading: summaryLoading } = useBillingSummary(tenantId)
  const { data: usage } = useUsageBreakdown(tenantId)
  const { data: addons } = useFeatureAddons(tenantId)
  const requestAddon = useRequestAddon(tenantId)
  const badgeColor = usePlanBadgeColor(summary?.plan || 'trial')

  const breadcrumbs = buildBreadcrumbs('/settings/account/subscription')

  const tabs = [
    { id: 'plan' as Tab, label: 'Plan & Limits', icon: Package },
    { id: 'features' as Tab, label: 'Premium Features', icon: Sparkles },
  ]

  return (
    <div>
      <PageHeader
        title="Subscription & Plan"
        description="Manage your subscription, view limits, and explore premium features."
        breadcrumbs={breadcrumbs}
      />

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-warm-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-copper-500 text-copper-700'
                : 'border-transparent text-warm-500 hover:text-warm-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'plan' && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="space-y-6 max-w-4xl">
          {/* Plan Overview */}
          <div className="rounded-xl border border-warm-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-midnight-800">Current Plan</h2>
              {summary && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${badgeColor}`}>
                  {summary.plan}
                </span>
              )}
            </div>
            {summaryLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-warm-100 rounded w-1/3" />
                <div className="h-4 bg-warm-100 rounded w-1/2" />
              </div>
            ) : summary ? (
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <dt className="text-xs text-warm-500 uppercase tracking-wide">Started</dt>
                  <dd className="text-sm font-medium text-midnight-800">{summary.started_at || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-warm-500 uppercase tracking-wide">Renewal</dt>
                  <dd className="text-sm font-medium text-midnight-800">{summary.renewal_date || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-warm-500 uppercase tracking-wide">Profiles</dt>
                  <dd className="text-sm font-medium text-midnight-800">{summary.profiles_used ?? 0} / {summary.max_profiles ?? '∞'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-warm-500 uppercase tracking-wide">API Keys</dt>
                  <dd className="text-sm font-medium text-midnight-800">{summary.api_keys_used ?? 0} / {summary.max_api_keys ?? '∞'}</dd>
                </div>
              </dl>
            ) : null}
          </div>

          {/* Usage */}
          {usage && (
            <div className="rounded-xl border border-warm-200 bg-white p-6">
              <h2 className="font-heading font-semibold text-midnight-800 mb-4">Usage</h2>
              <div className="space-y-4">
                {[
                  { label: 'Profiles', metric: usage.profiles },
                  { label: 'API Keys', metric: usage.api_keys },
                  { label: 'Rate Limit', metric: usage.rate_limit },
                ].map(({ label, metric }) => (
                  <div key={label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-warm-600">{label}</span>
                      <span className="text-midnight-800 font-medium">{metric.current} / {metric.limit}</span>
                    </div>
                    <div className="h-2 bg-warm-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-copper-500 rounded-full transition-all"
                        style={{ width: `${Math.min(metric.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'features' && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {addons?.map((addon) => (
              <div key={addon.key} className="rounded-xl border border-warm-200 bg-white p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-heading font-semibold text-midnight-800 text-sm">{addon.name}</h3>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                    addon.is_active ? 'bg-sage-50 text-sage-600' : 'bg-warm-100 text-warm-500'
                  }`}>
                    {addon.is_active ? 'Active' : 'Available'}
                  </span>
                </div>
                <p className="text-xs text-warm-500 mb-3">{addon.description}</p>
                {!addon.is_active && (
                  <button
                    onClick={() => requestAddon.mutate({ addonKey: addon.key })}
                    disabled={requestAddon.isPending}
                    className="flex items-center gap-1 text-xs font-medium text-copper-600 hover:text-copper-700"
                  >
                    Request Quote <ArrowUpRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}
